/**
 * EnrollmentQrPanel — show (and, deliberately, rotate) the DPC provisioning QR.
 *
 * Renders on the device detail page when the device is `android-do`.
 * "Show enrollment QR" → POST /api/devices/:imei/dpc-enrollment → response
 * includes the QR payload (the exact JSON Android's setup wizard reads) + the
 * device's plaintext snapshot key.
 *
 * Important UX rules:
 *
 *   - Showing the QR is IDEMPOTENT: it reshows the device's existing key, so
 *     you can reopen it as often as you need. It does NOT rotate — a device
 *     that already scanned this QR stays valid. (Rotating on every view is
 *     what used to orphan field devices onto a revoked key.)
 *   - "Rotate key…" (only for already-enrolled devices) is the deliberate,
 *     destructive re-key: forceRotate=true generates a new key + revokes the
 *     old one, which STOPS any handset still on the previous key. Confirmed
 *     behind a dialog.
 *   - The customer's handset must be factory-reset BEFORE scanning a FIRST
 *     enrollment, or the QR's setup-wizard path will not fire.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { QrCodeRenderer } from "./QrCodeRenderer.js";
import { api } from "../lib/api.js";

interface MintResponse {
  readonly imei: string;
  readonly deviceId: string;
  readonly enrollmentSerial: number;
  readonly bootstrapToken: string;
  readonly snapshotKeyHex: string;
  readonly leasePublicKeyB64: string;
  readonly leasePublicKeyVersion: number;
  readonly qrPayload: Record<string, unknown>;
}

interface Props {
  readonly imei: string;
  readonly enrollmentVerifiedAt: string | null;
}

export function EnrollmentQrPanel({ imei, enrollmentVerifiedAt }: Props): JSX.Element {
  const [mintResult, setMintResult] = useState<MintResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const mint = useMutation({
    // Default (forceRotate=false) RESHOWS the device's existing key — safe to
    // click repeatedly. forceRotate=true generates a new key and revokes the
    // old one, which orphans a handset already on the current QR.
    mutationFn: (forceRotate: boolean) =>
      api.post<MintResponse>(
        `/api/devices/${imei}/dpc-enrollment${forceRotate ? "?forceRotate=true" : ""}`,
        {},
      ),
    onSuccess: (res) => {
      setMintResult(res);
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Request refused");
      } else {
        setError("Network error");
      }
    },
  });

  function onRotate(): void {
    const ok = window.confirm(
      "Rotate the snapshot key?\n\nThis generates a NEW key and revokes the current one. Any " +
        "handset already provisioned with the current QR will STOP reporting — its snapshots get " +
        "rejected and its service PIN changes. Only rotate to recover a compromised key or to " +
        "re-provision a different handset.",
    );
    if (ok) mint.mutate(true);
  }

  const qrJson = mintResult ? JSON.stringify(mintResult.qrPayload, null, 2) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>DPC enrollment QR</CardTitle>
        <span className="text-xs text-slate-500">
          {enrollmentVerifiedAt ? "Reshows the existing key" : "Show QR to provision"}
        </span>
      </CardHeader>
      <CardBody className="space-y-4 text-sm">
        {!mintResult ? (
          <>
            <p className="text-slate-700">
              The customer's handset must be <strong>factory-reset</strong> and showing the Android
              setup wizard before scanning. Showing the QR reshows this device's{" "}
              <strong>existing</strong> key — safe to reopen as many times as you need; it never
              rotates.
            </p>
            {error ? (
              <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => mint.mutate(false)} disabled={mint.isPending}>
                {mint.isPending ? "Loading…" : "Show enrollment QR"}
              </Button>
              {enrollmentVerifiedAt ? (
                <Button variant="secondary" onClick={onRotate} disabled={mint.isPending}>
                  Rotate key…
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-3">
              <QrCodeRenderer payload={qrJson ?? ""} sizePx={280} />
              <p className="text-xs text-slate-500">
                Scan with the customer's setup-wizard QR camera. Serial #
                {mintResult.enrollmentSerial}.
              </p>
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs">
              <p className="font-semibold text-amber-900">Snapshot key (shown once)</p>
              {showKey ? (
                <>
                  <code className="break-all text-[10px]">{mintResult.snapshotKeyHex}</code>
                  <button
                    type="button"
                    className="mt-2 text-amber-900 underline"
                    onClick={() => setShowKey(false)}
                  >
                    Hide
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="text-amber-900 underline"
                  onClick={() => setShowKey(true)}
                >
                  Reveal
                </button>
              )}
            </div>
            <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              <summary className="cursor-pointer font-medium">QR payload (JSON)</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-[10px]">
                {qrJson}
              </pre>
            </details>
            <Button
              variant="secondary"
              onClick={() => {
                setMintResult(null);
                setShowKey(false);
              }}
            >
              Done
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}
